import { Client, Conversation, Message } from '@twilio/conversations';
import { apiClient } from '@/config/api';

/**
 * Twilio Conversations client wrapper.
 *
 * Owns the single Twilio Client instance and handles its lifecycle: token
 * fetch from /chat/token, refresh on `tokenAboutToExpire`, and clean teardown
 * on logout. The rest of the app talks through `twilioChat` rather than the
 * SDK directly so we can swap implementations or stub for tests.
 *
 * Messages themselves are read/sent through Twilio Conversation objects;
 * the SGA backend is not in the per-message hot path.
 */

interface ChatTokenResponse {
  identity: string;
  token: string;
}

class TwilioChat {
  private client: Client | null = null;
  private initPromise: Promise<Client> | null = null;
  private errorListeners: Array<(err: Error) => void> = [];

  /**
   * Subscribe to fatal client errors (token refresh failure, etc.) so the
   * UI can surface them instead of relying on silent console warnings.
   * Returns an unsubscribe.
   */
  onError(listener: (err: Error) => void): () => void {
    this.errorListeners.push(listener);
    return () => {
      this.errorListeners = this.errorListeners.filter((l) => l !== listener);
    };
  }

  private emitError(err: Error): void {
    for (const l of this.errorListeners) {
      try {
        l(err);
      } catch {
        /* listener errors must not propagate back into the SDK */
      }
    }
  }

  /** Lazy-init a single Client. Subsequent callers share the same instance. */
  async getClient(): Promise<Client> {
    if (this.client) {
      return this.client;
    }
    if (!this.initPromise) {
      this.initPromise = this.initialise();
    }
    return this.initPromise;
  }

  private async initialise(): Promise<Client> {
    const { token } = await this.fetchToken();
    const client = new Client(token);

    client.on('tokenAboutToExpire', () => this.refreshToken(client));
    client.on('tokenExpired', () => this.refreshToken(client));

    // Wait for the client to reach an initialised state before exposing it.
    await new Promise<void>((resolve, reject) => {
      const onState = (state: string) => {
        if (state === 'initialized') {
          client.off('stateChanged', onState);
          resolve();
        } else if (state === 'failed') {
          client.off('stateChanged', onState);
          reject(new Error('Twilio Conversations client failed to initialise'));
        }
      };
      client.on('stateChanged', onState);
    });

    this.client = client;
    return client;
  }

  private async fetchToken(): Promise<ChatTokenResponse> {
    return apiClient.post<ChatTokenResponse>('/chat/token');
  }

  private async refreshToken(client: Client): Promise<void> {
    try {
      const { token } = await this.fetchToken();
      await client.updateToken(token);
    } catch (e) {
      // A failed refresh is recoverable in principle (next attempt may
      // succeed) but the user needs to know — otherwise messaging will
      // silently stop working when the token expires.
      const err = e instanceof Error ? e : new Error(String(e));
      console.warn('[twilioChat] token refresh failed', err);
      this.emitError(new Error('Messaging session expired. Pull to refresh or sign in again.'));
    }
  }

  /** Subscribed conversations for the current identity, paginated by Twilio. */
  async getSubscribedConversations(): Promise<Conversation[]> {
    const client = await this.getClient();
    const paginator = await client.getSubscribedConversations();
    return [...paginator.items];
  }

  /** The Twilio identity the current Client is connected as. */
  async getIdentity(): Promise<string> {
    const client = await this.getClient();
    return client.user.identity;
  }

  async getConversation(twilioSid: string): Promise<Conversation> {
    const client = await this.getClient();
    return client.getConversationBySid(twilioSid);
  }

  async sendMessage(conversation: Conversation, body: string): Promise<Message> {
    if (!body.trim()) {
      throw new Error('Message body cannot be empty');
    }
    return conversation.sendMessage(body.trim()) as unknown as Promise<Message>;
  }

  /** Page of past messages, newest-last. */
  async getMessages(conversation: Conversation, count = 30): Promise<Message[]> {
    const page = await conversation.getMessages(count);
    return [...page.items];
  }

  /** Drop the client (used on logout). */
  async shutdown(): Promise<void> {
    if (this.client) {
      try {
        await this.client.shutdown();
      } catch {
        /* ignore */
      }
      this.client = null;
      this.initPromise = null;
    }
    // Listeners belong to the previous identity; don't replay errors to
    // whoever signs in next.
    this.errorListeners = [];
  }
}

const twilioChat = new TwilioChat();
export default twilioChat;
export type { Conversation, Message };
